// Obsidian-style navigation graph, in 3D. A force-directed "spider web" of the hub's links laid out in
// space: a central node connects out to group nodes and to the destination leaf nodes, the whole thing
// relaxes into an organic web, gently auto-rotates, and you can drag to orbit it. Hovering a node lifts
// it and its connections; clicking a leaf navigates. Rendered on a canvas with a simple perspective
// projection (no 3D library). Honors prefers-reduced-motion (no auto-rotation; static, still orbitable).
// On touch a tap stands in for hover: tapping a group or the centre pins its focus, tapping a page (its dot
// or its label) opens it. A List view shows the same pages as plain grouped rows: the phone default, and
// the screen-reader equivalent of the canvas.

import { useCallback, useEffect, useMemo, useRef, useState, type MutableRefObject, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PageHelpButton, Segmented } from './ui.js';
import { IconChevronRight, IconRefresh } from './icons.js';
import { useIsPhone } from '../lib/media.js';

export interface HubItem {
  label: string;
  path?: string;
  external?: string;
  group?: string;
  desc?: string;
}

/** imperative handle so a page's own controls (zoom, reset) can drive the graph. */
export interface HubControls {
  reset: () => void;
  zoomBy: (factor: number) => void;
}

export type HubView = 'map' | 'list';
const VIEW_KEY = 'rolodex.hubView';
function readView(): HubView | null {
  try {
    const v = window.localStorage.getItem(VIEW_KEY);
    return v === 'map' || v === 'list' ? v : null;
  } catch {
    return null; // private mode / blocked storage: fall back to the device default
  }
}

/** Map or List for the hub pages (Explore, Assembly, Management), remembered per device. Until the user
 *  picks one, phones open on the List (the map is one tap away) and everything else on the Map. */
export function useHubView(): [HubView, (v: HubView) => void] {
  const isPhone = useIsPhone();
  const [stored, setStored] = useState<HubView | null>(readView);
  const set = useCallback((v: HubView) => {
    setStored(v);
    try { window.localStorage.setItem(VIEW_KEY, v); } catch { /* not remembered, still switches */ }
  }, []);
  return [stored ?? (isPhone ? 'list' : 'map'), set];
}

/** the floating glass toolbar over a hub: Map | List, the map's zoom / reset, and the page notes "?". */
export function HubTools({ controls, view, onView }: {
  controls: MutableRefObject<HubControls | null>;
  view: HubView;
  onView: (v: HubView) => void;
}) {
  return (
    <div className="explore-tools ex-glass">
      {/* the map's own buttons come first, so Map | List never moves under the thumb when they appear */}
      {view === 'map' && (
        <>
          <button className="ex-btn" title="Zoom in" aria-label="Zoom in" onClick={() => controls.current?.zoomBy(1.2)}>+</button>
          <button className="ex-btn" title="Zoom out" aria-label="Zoom out" onClick={() => controls.current?.zoomBy(1 / 1.2)}>&#8722;</button>
          <button className="ex-btn" title="Reset view" aria-label="Reset view" onClick={() => controls.current?.reset()}><IconRefresh size={16} /></button>
        </>
      )}
      <Segmented value={view} onChange={onView} ariaLabel="View" options={[{ value: 'map', label: 'Map' }, { value: 'list', label: 'List' }]} />
      <PageHelpButton />
    </div>
  );
}

/** the List view: the hub's pages as 48px rows, grouped the way the map clusters them. */
function HubList({ center, items }: { center: string; items: HubItem[] }) {
  const groups = useMemo(() => {
    const out: Array<{ name: string; items: HubItem[] }> = [];
    for (const it of items) {
      const name = it.group ?? '';
      let g = out.find((x) => x.name === name);
      if (!g) out.push((g = { name, items: [] }));
      g.items.push(it);
    }
    return out;
  }, [items]);
  const row = (it: HubItem) => (
    <>
      <span className="hg-list-txt">
        <span className="hg-list-label">{it.label}</span>
        {it.desc && <span className="hg-list-desc">{it.desc}</span>}
      </span>
      <IconChevronRight size={16} className="hg-list-chev" />
    </>
  );
  return (
    <nav className="hg-list" aria-label={`${center} pages`}>
      <div className="hg-list-in">
        {groups.map((g) => (
          <section key={g.name || '-'} className="hg-list-grp">
            {g.name && <h2 className="hg-list-hd">{g.name}</h2>}
            <ul>
              {g.items.map((it) => (
                <li key={it.label}>
                  {it.external
                    ? <a className="hg-list-row" href={it.external} target="_blank" rel="noopener noreferrer">{row(it)}</a>
                    : <Link className="hg-list-row" to={it.path ?? '/'}>{row(it)}</Link>}
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    </nav>
  );
}

type Kind = 'center' | 'group' | 'leaf';
interface GNode {
  id: string;
  label: string;
  kind: Kind;
  path?: string;
  external?: string;
  parent: string | null;
  r: number; // base radius in graph units
}
interface GEdge { from: string; to: string; cross: boolean; L: number; k: number }
interface P { x: number; y: number; z: number; vx: number; vy: number; vz: number }

const CENTER_ID = '__center';
const CAM = 1500;      // camera distance for the perspective divide
const MAX_R = 340;     // nodes are kept inside this sphere

function restLen(a: Kind, b: Kind): number {
  const s = new Set([a, b]);
  if (s.has('center') && s.has('group')) return 160;
  if (s.has('group') && s.has('leaf')) return 82;
  return 130; // center ↔ ungrouped leaf
}

// Resolve a colour token to a concrete rgb()/rgba() the 2D canvas can always parse. Theme tokens are
// now palette-tinted with color-mix(), and a raw color-mix() string is evaluated by canvas only on very
// recent engines — so we bounce it through a hidden probe once and cache the result (keyed on the raw
// string, which already has its var()s substituted, so a theme/palette switch just produces a fresh key).
const _colorCache = new Map<string, string>();
let _probe: HTMLSpanElement | null = null;
function resolveColor(v: string): string {
  if (!v || (!v.includes('color-mix') && !v.includes('var('))) return v;
  const hit = _colorCache.get(v);
  if (hit) return hit;
  if (!_probe) {
    _probe = document.createElement('span');
    _probe.style.cssText = 'position:absolute;opacity:0;pointer-events:none;width:0;height:0';
    document.body.appendChild(_probe);
  }
  _probe.style.color = '';
  _probe.style.color = v;
  const resolved = getComputedStyle(_probe).color || v;
  _colorCache.set(v, resolved);
  return resolved;
}

// read a CSS custom property off an element (so the canvas tracks the app's light/dark theme).
function cssVar(el: Element, name: string, fallback: string): string {
  const v = getComputedStyle(el).getPropertyValue(name).trim();
  return resolveColor(v || fallback);
}

export function HubGraph({
  center,
  items,
  links,
  fullBleed,
  controlsRef,
  view = 'map',
  children,
}: {
  center: string;
  items: HubItem[];
  /** extra edges between leaf nodes (by label) — the cross-links that turn a star into a web. */
  links?: Array<[string, string]>;
  /** immersive, edge-to-edge presentation with no surrounding box. */
  fullBleed?: boolean;
  /** populated with zoom/reset handles so a page can drive the graph from its own controls. */
  controlsRef?: MutableRefObject<HubControls | null>;
  /** 'list' swaps the canvas for plain grouped rows of the same pages (see useHubView). */
  view?: HubView;
  /** overlay content (e.g. floating glass controls) rendered above the canvas. */
  children?: ReactNode;
}) {
  const navigate = useNavigate();
  const wrapRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [cursor, setCursor] = useState<'grab' | 'grabbing' | 'pointer'>('grab');

  const { nodes, edges, adj } = useMemo(() => {
    const groups: string[] = [];
    for (const it of items) if (it.group && !groups.includes(it.group)) groups.push(it.group);
    const gid = (g: string) => `grp:${g}`;
    const nodes: GNode[] = [{ id: CENTER_ID, label: center, kind: 'center', parent: null, r: 15 }];
    for (const g of groups) nodes.push({ id: gid(g), label: g, kind: 'group', parent: CENTER_ID, r: 9 });
    items.forEach((it, i) =>
      nodes.push({
        id: `leaf:${i}`,
        label: it.label,
        kind: 'leaf',
        path: it.path,
        external: it.external,
        parent: it.group ? gid(it.group) : CENTER_ID,
        r: 5.5,
      }),
    );
    const byId = new Map(nodes.map((n) => [n.id, n]));
    const idByLabel = new Map<string, string>();
    for (const n of nodes) if (n.kind === 'leaf') idByLabel.set(n.label, n.id);
    const edges: GEdge[] = [];
    for (const n of nodes) if (n.parent) edges.push({ from: n.parent, to: n.id, cross: false, L: restLen(n.kind, byId.get(n.parent)!.kind), k: 0.02 });
    for (const [a, b] of links ?? []) {
      const ia = idByLabel.get(a), ib = idByLabel.get(b);
      if (ia && ib && ia !== ib) edges.push({ from: ia, to: ib, cross: true, L: 115, k: 0.008 });
    }
    const adj = new Map<string, Set<string>>(nodes.map((n) => [n.id, new Set<string>()]));
    for (const e of edges) { adj.get(e.from)!.add(e.to); adj.get(e.to)!.add(e.from); }
    return { nodes, edges, adj };
  }, [center, items, links]);

  useEffect(() => {
    const canvas = canvasRef.current, wrap = wrapRef.current;
    if (!canvas || !wrap) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const reduced = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // ── state ──────────────────────────────────────────────────────────────
    const pos = new Map<string, P>();
    const fib = (i: number, n: number, R: number) => {
      const phi = Math.acos(1 - 2 * (i + 0.5) / Math.max(n, 1));
      const theta = Math.PI * (1 + Math.sqrt(5)) * i;
      return { x: R * Math.sin(phi) * Math.cos(theta), y: R * Math.cos(phi), z: R * Math.sin(phi) * Math.sin(theta) };
    };
    const groups = nodes.filter((n) => n.kind === 'group');
    pos.set(CENTER_ID, { x: 0, y: 0, z: 0, vx: 0, vy: 0, vz: 0 });
    groups.forEach((g, i) => { const p = fib(i, groups.length, 150); pos.set(g.id, { ...p, vx: 0, vy: 0, vz: 0 }); });
    const leaves = nodes.filter((n) => n.kind === 'leaf');
    let ung = 0;
    const ungCount = leaves.filter((l) => l.parent === CENTER_ID).length;
    leaves.forEach((lf) => {
      const gp = lf.parent && lf.parent !== CENTER_ID ? pos.get(lf.parent) : null;
      const jit = () => (Math.random() - 0.5) * 70;
      const seed = gp ? { x: gp.x + jit(), y: gp.y + jit(), z: gp.z + jit() } : fib(ung++, ungCount, 130);
      pos.set(lf.id, { x: seed.x, y: seed.y, z: seed.z, vx: 0, vy: 0, vz: 0 });
    });

    const BASE_ROTY = 0.5, BASE_ROTX = -0.35;
    let rotY = BASE_ROTY, rotX = BASE_ROTX;
    let userZoom = 1, targetZoom = 1;           // pinch / wheel zoom, eased toward target
    let spinVY = 0, spinVX = 0;                 // rotational momentum after a flick (velocity handoff)
    let parX = 0, parY = 0, parTX = 0, parTY = 0; // pointer-reactive parallax, layered over rotX/rotY
    let dragging = false, moved = 0, lastX = 0, lastY = 0, lastT = 0, downTarget: GNode | null = null;
    let hover: string | null = null, hoverAmt = 0; // hoverAmt eases 0..1 for a spring-like emphasis
    let settled = false, frames = 0, raf = 0;
    let w = 0, h = 0, dpr = 1;
    let fitR = 0; // eased 3D radius of the laid-out web (rotation-invariant, so turning never rescales it)
    // multi-pointer tracking for pinch-to-zoom; single pointer = orbit.
    const pointers = new Map<number, { x: number; y: number }>();
    let pinching = false, pinchStartDist = 0, pinchStartZoom = 1;
    const fine = typeof window.matchMedia === 'function' && window.matchMedia('(pointer: fine)').matches;
    const coarse = typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches;
    let touched = false; // once a finger has been on the map, idle auto-rotation stops (it moved the targets)
    const clampZoom = (z: number) => Math.max(0.45, Math.min(3.2, z));
    const now = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());
    // projected screen positions from the last draw, for hit-testing on pointer move; `lb` is the drawn
    // label's box, which touch also accepts (a thumb lands on the word, not the dot).
    type LabelBox = { x0: number; y0: number; x1: number; y1: number };
    let proj: Array<{ node: GNode; sx: number; sy: number; sr: number; depth: number; lb: LabelBox | null }> = [];

    const resize = () => {
      const r = wrap.getBoundingClientRect();
      w = Math.max(320, r.width); h = Math.max(200, r.height);
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.round(w * dpr); canvas.height = Math.round(h * dpr);
      canvas.style.width = `${w}px`; canvas.style.height = `${h}px`;
      if (reduced) draw();
    };

    // ── physics (3D) ─────────────────────────────────────────────────────────
    const step = (): number => {
      for (let i = 0; i < nodes.length; i++) {
        const a = pos.get(nodes[i]!.id)!;
        for (let j = i + 1; j < nodes.length; j++) {
          const b = pos.get(nodes[j]!.id)!;
          let dx = a.x - b.x, dy = a.y - b.y, dz = a.z - b.z;
          let d2 = dx * dx + dy * dy + dz * dz;
          if (d2 < 0.01) { dx = 0.6; dy = 0.4; dz = 0.5; d2 = 0.77; }
          const d = Math.sqrt(d2);
          const f = Math.min(13000 / d2, 30);
          const ux = dx / d, uy = dy / d, uz = dz / d;
          a.vx += ux * f; a.vy += uy * f; a.vz += uz * f;
          b.vx -= ux * f; b.vy -= uy * f; b.vz -= uz * f;
        }
      }
      for (const e of edges) {
        const a = pos.get(e.from)!, b = pos.get(e.to)!;
        const dx = b.x - a.x, dy = b.y - a.y, dz = b.z - a.z;
        const d = Math.hypot(dx, dy, dz) || 1;
        const f = e.k * (d - e.L);
        const ux = dx / d, uy = dy / d, uz = dz / d;
        a.vx += ux * f; a.vy += uy * f; a.vz += uz * f;
        b.vx -= ux * f; b.vy -= uy * f; b.vz -= uz * f;
      }
      let ke = 0;
      for (const n of nodes) {
        const p = pos.get(n.id)!;
        if (n.id === CENTER_ID) { p.x = p.y = p.z = 0; p.vx = p.vy = p.vz = 0; continue; }
        p.vx += -0.003 * p.x; p.vy += -0.003 * p.y; p.vz += -0.003 * p.z;
        p.vx *= 0.86; p.vy *= 0.86; p.vz *= 0.86;
        const sp = Math.hypot(p.vx, p.vy, p.vz);
        if (sp > 24) { const s = 24 / sp; p.vx *= s; p.vy *= s; p.vz *= s; }
        p.x += p.vx; p.y += p.vy; p.z += p.vz;
        const rad = Math.hypot(p.x, p.y, p.z);
        if (rad > MAX_R) { const s = MAX_R / rad; p.x *= s; p.y *= s; p.z *= s; }
        ke += sp * sp;
      }
      return ke;
    };

    // ── render ────────────────────────────────────────────────────────────────
    const draw = () => {
      const root = document.documentElement;
      const cAccent = cssVar(root, '--accent', '#8a9bb0'); // neutral grey until the palette sets --accent
      const cText = cssVar(canvas, '--text-strong', cssVar(canvas, '--text', '#e8eef5'));
      const cMuted = cssVar(canvas, '--text-muted', '#8a9bb0');
      const cLine = cssVar(canvas, '--border', 'rgba(128,128,128,.25)');
      const cSurface = cssVar(canvas, '--surface', '#141b23');
      // the canvas font setter rejects var(), so resolve the family here (once per frame).
      const fontBody = getComputedStyle(root).getPropertyValue('--font-body').trim() || 'system-ui, sans-serif';

      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, w, h);
      const cx = w / 2, cy = h / 2;
      // fit the web to the canvas: the old 0.55 floor overflowed a phone, so the width term keeps the web
      // (its real radius, x1.25 for perspective on the near side, plus room for labels) inside half the
      // width. A 1400x846 desktop still lands near 1.03, from the min(w,h) term.
      let rMax = 120;
      for (const p of pos.values()) rMax = Math.max(rMax, Math.hypot(p.x, p.y, p.z));
      fitR = reduced || !fitR ? rMax : fitR + (rMax - fitR) * 0.15;
      const base = Math.max(0.3, Math.min(1.45, Math.min(w, h) / 820, (w / 2 - 56) / (fitR * 1.25)));
      const zoom = base * userZoom;
      // parallax is a gentle look-toward-cursor offset layered over the orbit (never accumulates).
      const ay = rotY + parY;
      const ax = Math.max(-1.4, Math.min(1.4, rotX + parX));
      const cosY = Math.cos(ay), sinY = Math.sin(ay), cosX = Math.cos(ax), sinX = Math.sin(ax);
      const project = (p: { x: number; y: number; z: number }) => {
        const x1 = p.x * cosY + p.z * sinY;
        const z1 = -p.x * sinY + p.z * cosY;
        const y1 = p.y * cosX - z1 * sinX;
        const z2 = p.y * sinX + z1 * cosX;
        const denom = Math.max(CAM - z2, 250);
        const persp = CAM / denom;
        return { sx: cx + x1 * persp * zoom, sy: cy - y1 * persp * zoom, depth: z2, scale: persp * zoom };
      };

      const P2 = new Map<string, { sx: number; sy: number; depth: number; scale: number }>();
      for (const n of nodes) P2.set(n.id, project(pos.get(n.id)!));
      proj = nodes.map((n) => { const q = P2.get(n.id)!; return { node: n, sx: q.sx, sy: q.sy, sr: Math.max(n.r * q.scale, 4), depth: q.depth, lb: null }; });

      const near = (id: string) => !hover || id === hover || adj.get(hover)?.has(id);
      const depthAlpha = (d: number) => 0.45 + 0.55 * ((d + MAX_R) / (2 * MAX_R)); // farther = fainter

      // edges (drawn first, back-to-front by mid-depth)
      const sortedEdges = [...edges].sort((a, a2) => (P2.get(a.from)!.depth + P2.get(a.to)!.depth) - (P2.get(a2.from)!.depth + P2.get(a2.to)!.depth));
      for (const e of sortedEdges) {
        const a = P2.get(e.from)!, b = P2.get(e.to)!;
        const on = hover && (e.from === hover || e.to === hover);
        const lit = near(e.from) && near(e.to);
        const sc = (a.scale + b.scale) / 2;              // nearer connections read thicker (depth cue)
        ctx.beginPath();
        ctx.moveTo(a.sx, a.sy); ctx.lineTo(b.sx, b.sy);
        ctx.strokeStyle = on ? cAccent : cLine;
        ctx.globalAlpha = (on ? 0.9 : lit ? 0.5 : 0.12) * depthAlpha((a.depth + b.depth) / 2);
        ctx.lineWidth = (e.cross ? 0.8 : 1.2) * (0.7 + 0.7 * sc);
        if (e.cross && !on) ctx.setLineDash([3, 4]); else ctx.setLineDash([]);
        ctx.stroke();
      }
      ctx.setLineDash([]);
      ctx.globalAlpha = 1;

      // ── a light sweep runs along each connection ───────────────────────────────
      // No floating particles: the connection LINE itself brightens in a moving band that travels from
      // the hub outward (parent → child), like a pulse of light running down a wire. The motion stays on
      // the wires, so it's easy to read. One slow band per edge; the hovered node's own links sweep a
      // touch brighter and faster. Built as a linear gradient stroked over the base line with additive
      // blending, so the band glows and the rest of the line is untouched.
      if (!reduced) {
        const tms = now();
        ctx.globalCompositeOperation = 'lighter';
        ctx.lineCap = 'round';
        const W = 0.16; // half-width of the bright band, as a fraction of the edge length
        for (let ei = 0; ei < sortedEdges.length; ei++) {
          const e = sortedEdges[ei]!;
          const on = !!hover && (e.from === hover || e.to === hover);
          const lit = near(e.from) && near(e.to);
          if (hover && !lit) continue;                   // focus: only the hovered subtree sweeps
          const a = P2.get(e.from)!, b = P2.get(e.to)!;
          const dx = b.sx - a.sx, dy = b.sy - a.sy;
          if (dx * dx + dy * dy < 36) continue;          // too short on screen to bother
          const speed = on ? 0.00032 : 0.00022;          // ~3-4.5s to run the length of an edge
          const t = ((tms + ei * 760) * speed) % 1;      // band centre, travelling parent → child
          const bright = on ? 0.95 : lit ? 0.55 : 0.3;
          const g = ctx.createLinearGradient(a.sx, a.sy, b.sx, b.sy);
          const lo = t - W, hi = t + W;
          g.addColorStop(0, 'transparent');
          if (lo > 0) g.addColorStop(lo, 'transparent');
          g.addColorStop(Math.min(1, Math.max(0, t)), cAccent);
          if (hi < 1) g.addColorStop(hi, 'transparent');
          g.addColorStop(1, 'transparent');
          ctx.strokeStyle = g;
          ctx.globalAlpha = bright * depthAlpha((a.depth + b.depth) / 2);
          ctx.lineWidth = (e.cross ? 1.3 : 1.9) * (0.7 + 0.6 * ((a.scale + b.scale) / 2));
          ctx.beginPath();
          ctx.moveTo(a.sx, a.sy); ctx.lineTo(b.sx, b.sy);
          ctx.stroke();
        }
        ctx.globalCompositeOperation = 'source-over';
        ctx.globalAlpha = 1;
        ctx.lineCap = 'butt';
      }

      // nodes + labels, back-to-front
      const order = [...proj].sort((a, b) => a.depth - b.depth);
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      for (const q of order) {
        const n = q.node;
        const lit = near(n.id);
        const da = depthAlpha(q.depth);
        const emph = hover === n.id ? hoverAmt : 0;       // the hovered node lifts + glows
        const r = q.sr * (1 + 0.42 * emph);
        ctx.globalAlpha = (lit ? 1 : 0.28) * da;
        const fill = n.kind === 'leaf' ? cSurface : cAccent;
        if (emph > 0.01) { ctx.shadowColor = cAccent; ctx.shadowBlur = 18 * emph; } else { ctx.shadowBlur = 0; }
        ctx.beginPath();
        ctx.arc(q.sx, q.sy, r, 0, Math.PI * 2);
        ctx.fillStyle = fill;
        ctx.fill();
        ctx.shadowBlur = 0;
        if (n.kind === 'leaf') { ctx.lineWidth = 1.5 + emph; ctx.strokeStyle = cAccent; ctx.stroke(); }
        if (hover === n.id) { ctx.lineWidth = 2; ctx.strokeStyle = cAccent; ctx.stroke(); }
        // specular highlight — a small offset light bloom top-left, so the node reads as a lit sphere
        if (r > 3.5) {
          ctx.globalAlpha = (lit ? 0.5 : 0.16) * da;
          ctx.beginPath();
          ctx.arc(q.sx - r * 0.32, q.sy - r * 0.34, r * 0.42, 0, Math.PI * 2);
          ctx.fillStyle = '#ffffff';
          ctx.fill();
          ctx.globalAlpha = (lit ? 1 : 0.28) * da;
        }
        // label: kept whole inside the canvas (an edge node no longer loses half its name)
        const showLabel = n.kind !== 'leaf' || lit;
        if (showLabel) {
          // touch reads at arm's length: 12/13/14px, growing a little as the user zooms in
          const fs0 = coarse
            ? (n.kind === 'center' ? 14 : n.kind === 'group' ? 13 : 12) * Math.max(0.85, Math.min(1.25, userZoom))
            : n.kind === 'center' ? 13 : n.kind === 'group' ? 11.5 : 10.5;
          const fs = fs0 * (1 + 0.18 * emph);
          ctx.font = `${n.kind === 'leaf' ? 500 : 600} ${fs}px ${fontBody}`;
          ctx.globalAlpha = (lit ? 1 : 0.4) * da;
          const ly = q.sy + r + fs * 0.9 + 2;
          const tw = ctx.measureText(n.label).width;
          const lx = Math.min(Math.max(q.sx, tw / 2 + 6), w - tw / 2 - 6);
          ctx.fillStyle = n.kind === 'leaf' ? cMuted : cText;
          ctx.fillText(n.label, lx, ly);
          q.lb = { x0: lx - tw / 2 - 4, y0: ly - fs * 0.7, x1: lx + tw / 2 + 4, y1: ly + fs * 0.7 };
        }
      }
      ctx.globalAlpha = 1;
    };

    // ── loop ────────────────────────────────────────────────────────────────
    const loop = () => {
      // eased zoom (pinch / wheel), parallax and hover emphasis — spring-ish lerps for a fluid feel.
      userZoom += (targetZoom - userZoom) * 0.18; if (Math.abs(targetZoom - userZoom) < 0.0005) userZoom = targetZoom;
      parX += (parTX - parX) * 0.07; parY += (parTY - parY) * 0.07;
      hoverAmt += ((hover ? 1 : 0) - hoverAmt) * 0.22;
      if (!dragging && !pinching) {
        if (Math.hypot(spinVX, spinVY) > 0.0004) {
          // momentum after a flick, decaying (velocity handoff from the drag)
          rotY += spinVY; rotX = Math.max(-1.35, Math.min(1.35, rotX + spinVX));
          spinVY *= 0.94; spinVX *= 0.94;
        } else if (!reduced && !touched) {
          rotY += 0.0022; // gentle idle auto-rotation
        }
      }
      if (!settled) { frames++; if (step() < 0.5 || frames > 900) settled = true; }
      draw();
      const keepGoing = dragging || pinching || !reduced || !settled
        || Math.abs(targetZoom - userZoom) > 0.0005
        || Math.hypot(spinVX, spinVY) > 0.0004
        || Math.abs(parTX - parX) > 0.0008 || Math.abs(parTY - parY) > 0.0008;
      raf = keepGoing ? requestAnimationFrame(loop) : 0;
    };
    const kick = () => { if (!raf) raf = requestAnimationFrame(loop); };

    // ── interaction ───────────────────────────────────────────────────────────
    // mouse: the dot (+9px), nearest-to-the-viewer wins, as before. Touch: a finger-sized ring around the
    // dot (+22px) or the label's box, and the closest of those wins, so dense clusters stay pickable.
    const pick = (clientX: number, clientY: number, touch = false): GNode | null => {
      const r = canvas.getBoundingClientRect();
      const px = clientX - r.left, py = clientY - r.top;
      let best: { node: GNode; d: number; depth: number } | null = null;
      for (const q of proj) {
        const d = Math.hypot(px - q.sx, py - q.sy);
        if (!touch) {
          if (d <= q.sr + 9 && (!best || q.depth > best.depth)) best = { node: q.node, d, depth: q.depth };
          continue;
        }
        let hit = d <= q.sr + 22 ? d : Infinity;
        const b = q.lb;
        if (b && px >= b.x0 && px <= b.x1 && py >= b.y0 && py <= b.y1) {
          // on the words: rank by how close to the text's line the finger is (labels are wide, not tall)
          hit = Math.min(hit, Math.abs(py - (b.y0 + b.y1) / 2) + Math.abs(px - (b.x0 + b.x1) / 2) * 0.1);
        }
        if (hit === Infinity) continue;
        if (!best || hit < best.d || (hit === best.d && q.depth > best.depth)) best = { node: q.node, d: hit, depth: q.depth };
      }
      return best?.node ?? null;
    };
    const onDown = (e: PointerEvent) => {
      if (e.pointerType !== 'mouse') touched = true;
      pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      try { canvas.setPointerCapture(e.pointerId); } catch { /* ignore */ }
      if (pointers.size >= 2) {
        // a second finger switches from orbit to pinch-to-zoom
        dragging = false; pinching = true; downTarget = null;
        const pts = [...pointers.values()];
        pinchStartDist = Math.hypot(pts[0]!.x - pts[1]!.x, pts[0]!.y - pts[1]!.y) || 1;
        pinchStartZoom = targetZoom;
      } else {
        dragging = true; moved = 0; lastX = e.clientX; lastY = e.clientY; lastT = now();
        spinVX = 0; spinVY = 0;
        downTarget = pick(e.clientX, e.clientY, e.pointerType !== 'mouse');
        setCursor('grabbing');
      }
      kick();
    };
    const onMove = (e: PointerEvent) => {
      if (pointers.has(e.pointerId)) pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (pinching && pointers.size >= 2) {
        const pts = [...pointers.values()];
        const dist = Math.hypot(pts[0]!.x - pts[1]!.x, pts[0]!.y - pts[1]!.y) || 1;
        targetZoom = clampZoom(pinchStartZoom * (dist / pinchStartDist));
        if (reduced) { userZoom = targetZoom; draw(); } else kick();
        return;
      }
      if (dragging) {
        const t = now(); const dt = Math.max(8, t - lastT); lastT = t;
        const dx = e.clientX - lastX, dy = e.clientY - lastY;
        lastX = e.clientX; lastY = e.clientY; moved += Math.abs(dx) + Math.abs(dy);
        const dRotY = dx * 0.008, dRotX = dy * 0.008;
        rotY += dRotY;
        rotX = Math.max(-1.35, Math.min(1.35, rotX + dRotX));
        if (!reduced) {
          // capture release velocity (normalised per ~16ms frame), blended and capped for a clean flick
          const s = 16 / dt;
          spinVY = Math.max(-0.07, Math.min(0.07, dRotY * s * 0.5 + spinVY * 0.5));
          spinVX = Math.max(-0.07, Math.min(0.07, dRotX * s * 0.5 + spinVX * 0.5));
        }
        if (reduced) draw();
      } else {
        const n = pick(e.clientX, e.clientY);
        const id = n?.id ?? null;
        if (id !== hover) { hover = id; if (reduced) { hoverAmt = id ? 1 : 0; draw(); } }
        setCursor(n?.kind === 'leaf' ? 'pointer' : 'grab');
        if (fine && !reduced) {
          const r = canvas.getBoundingClientRect();
          const nx = (e.clientX - r.left - w / 2) / (w / 2 || 1);
          const ny = (e.clientY - r.top - h / 2) / (h / 2 || 1);
          parTY = Math.max(-1, Math.min(1, nx)) * 0.12;
          parTX = Math.max(-1, Math.min(1, ny)) * 0.09;
          kick();
        }
      }
    };
    const onUp = (e: PointerEvent) => {
      pointers.delete(e.pointerId);
      try { canvas.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
      if (pinching) {
        if (pointers.size < 2) {
          pinching = false;
          const rest = [...pointers.values()][0]; // resume orbit from the finger still down
          if (rest) { dragging = true; moved = 999; lastX = rest.x; lastY = rest.y; lastT = now(); }
        }
        return;
      }
      const wasDrag = moved > 6;
      dragging = false;
      setCursor('grab');
      if (!wasDrag && downTarget && downTarget.kind === 'leaf') {
        if (downTarget.external) window.open(downTarget.external, '_blank', 'noopener');
        else if (downTarget.path) navigate(downTarget.path);
      } else if (!wasDrag && e.pointerType !== 'mouse') {
        // touch has no hover: a tap on the centre or a group pins its focus (tap again, or on empty
        // canvas, to let go), the same lift-and-dim a mouse gets by pointing at it.
        const id = downTarget ? (hover === downTarget.id ? null : downTarget.id) : null;
        if (id !== hover) { hover = id; if (reduced) { hoverAmt = id ? 1 : 0; draw(); } }
      }
      downTarget = null;
      kick();
    };
    const onLeave = (e: PointerEvent) => {
      // a lifted finger also "leaves": keep a tapped focus; a mouse or pen moving off clears it
      if (hover && e.pointerType !== 'touch') { hover = null; if (reduced) { hoverAmt = 0; draw(); } }
      parTX = 0; parTY = 0;
      if (!reduced) kick();
    };
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      targetZoom = clampZoom(targetZoom * (e.deltaY < 0 ? 1.12 : 1 / 1.12));
      if (reduced) { userZoom = targetZoom; draw(); } else kick();
    };

    // ── wire up ────────────────────────────────────────────────────────────────
    resize();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(resize) : null;
    ro?.observe(wrap);
    canvas.addEventListener('pointerdown', onDown);
    canvas.addEventListener('pointermove', onMove);
    canvas.addEventListener('pointerup', onUp);
    canvas.addEventListener('pointercancel', onUp);
    canvas.addEventListener('pointerleave', onLeave);
    canvas.addEventListener('wheel', onWheel, { passive: false });
    const onVis = () => { if (document.hidden) { if (raf) cancelAnimationFrame(raf), (raf = 0); } else kick(); };
    document.addEventListener('visibilitychange', onVis);

    // expose zoom / reset so the page's own floating controls can drive the graph.
    if (controlsRef) {
      controlsRef.current = {
        reset: () => {
          targetZoom = 1; rotX = BASE_ROTX; rotY = BASE_ROTY; spinVX = 0; spinVY = 0; parTX = 0; parTY = 0;
          if (reduced) { userZoom = 1; parX = 0; parY = 0; draw(); } else kick();
        },
        zoomBy: (f: number) => { targetZoom = clampZoom(targetZoom * f); if (reduced) { userZoom = targetZoom; draw(); } else kick(); },
      };
    }

    if (reduced) { for (let i = 0; i < 320; i++) step(); settled = true; draw(); }
    else kick();

    return () => {
      if (raf) cancelAnimationFrame(raf);
      ro?.disconnect();
      canvas.removeEventListener('pointerdown', onDown);
      canvas.removeEventListener('pointermove', onMove);
      canvas.removeEventListener('pointerup', onUp);
      canvas.removeEventListener('pointercancel', onUp);
      canvas.removeEventListener('pointerleave', onLeave);
      canvas.removeEventListener('wheel', onWheel);
      document.removeEventListener('visibilitychange', onVis);
      if (controlsRef) controlsRef.current = null;
    };
  }, [nodes, edges, adj, navigate, controlsRef, view]);

  if (items.length === 0) return null;
  return (
    <div ref={wrapRef} className={`hg-wrap${fullBleed ? ' hg-full' : ''}${view === 'list' ? ' is-list' : ''}`}>
      {view === 'list'
        ? <HubList center={center} items={items} />
        : <canvas ref={canvasRef} className="hg-canvas" style={{ display: 'block', touchAction: 'none', cursor }} role="group" aria-label={`${center} navigation graph (3D)`} />}
      {children}
    </div>
  );
}
